import type { Rule } from 'antd/es/form';

/**
 * TCR-2432 — Email nhận hoá đơn cho phép NHIỀU địa chỉ trong MỘT ô.
 *
 * Hợp đồng với BE: field vẫn là `tax_email` / `buyer_email` (một chuỗi), giá trị
 * là danh sách email cách nhau bằng dấu phẩy, ví dụ `a@x.com,b@y.com`. BE tách
 * theo `[,;\n]+`, khử trùng lặp và giới hạn tối đa 5 email. Toàn bộ logic phía
 * FE ở đây phải khớp đúng luật đó để người dùng không gặp cảnh FE cho lưu nhưng
 * BE trả 400 (hoặc ngược lại).
 */

/** Số email tối đa trong một ô — khớp validator của BE. */
export const MAX_EMAILS_PER_FIELD = 5;

/**
 * Regex của TỪNG email — giữ NGUYÊN regex đang dùng ở `CustomerModal` trước đây
 * (một email đơn), làm BƯỚC 1 để không đổi độ chặt ban đầu của từng địa chỉ.
 *
 * Regex này bỏ lọt vài domain mà BE (validator.js `isEmail`) từ chối, nên sau nó
 * còn `hasValidEmailStructure()` siết thêm phần domain/local-part — xem chú
 * thích ở hàm đó.
 */
const SINGLE_EMAIL_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Dấu phân cách: phẩy, chấm phẩy hoặc xuống dòng (dán từ Excel/Outlook). */
const SEPARATOR_PATTERN = /[,;\n]+/;

/** RFC 5321 — local-part tối đa 64 ký tự, cả địa chỉ tối đa 254 ký tự. */
const MAX_LOCAL_PART_LENGTH = 64;
const MAX_EMAIL_LENGTH = 254;
/** DNS label tối đa 63 ký tự (isFQDN của BE cũng chặn mốc này). */
const MAX_DOMAIN_LABEL_LENGTH = 63;

/**
 * Kiểm tra bổ sung phần domain/local-part mà regex cũ bỏ lọt, để FE không cho
 * qua những địa chỉ mà BE (validator.js `isEmail` → `isFQDN`) sẽ trả 400:
 * - domain tách theo '.', mỗi nhãn phải không rỗng (`x..com`), không bắt đầu/
 *   kết thúc bằng '-' (`-x.com`, `x-.com`) và chỉ chứa [a-zA-Z0-9-];
 * - TLD ≥ 2 chữ cái;
 * - local-part ≤ 64 ký tự, toàn địa chỉ ≤ 254 ký tự.
 *
 * Regex cũ vẫn là bước 1 (giữ nguyên độ chặt ban đầu); hàm này chỉ siết thêm.
 */
function hasValidEmailStructure(email: string): boolean {
  if (email.length > MAX_EMAIL_LENGTH) return false;
  const atIndex = email.lastIndexOf('@');
  if (atIndex <= 0) return false;
  const localPart = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  if (localPart.length > MAX_LOCAL_PART_LENGTH) return false;
  if (!domain) return false;

  const labels = domain.split('.');
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (!label) return false;
    if (label.length > MAX_DOMAIN_LABEL_LENGTH) return false;
    if (label.startsWith('-') || label.endsWith('-')) return false;
    if (!/^[a-zA-Z0-9-]+$/.test(label)) return false;
  }
  const tld = labels[labels.length - 1];
  return /^[a-zA-Z]{2,}$/.test(tld);
}

/** Một địa chỉ email hợp lệ theo đúng độ chặt của BE. */
function isSingleEmailValid(email: string): boolean {
  return SINGLE_EMAIL_PATTERN.test(email) && hasValidEmailStructure(email);
}

/**
 * Tách chuỗi người dùng nhập thành danh sách email sạch:
 * tách theo `[,;\n]+`, trim từng phần, bỏ phần rỗng, khử trùng lặp
 * (giữ lần xuất hiện đầu tiên để thứ tự hiển thị ổn định).
 */
export function parseEmailList(value: string | null | undefined): string[] {
  if (typeof value !== 'string') return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of value.split(SEPARATOR_PATTERN)) {
    const email = raw.trim();
    if (!email) continue;
    // So trùng không phân biệt hoa/thường để `A@x.com` và `a@x.com` không bị
    // coi là hai người nhận khác nhau.
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}

/** Chuỗi gửi lên BE: danh sách đã chuẩn hoá, KHÔNG có khoảng trắng thừa. */
export function serializeEmailList(value: string | null | undefined): string {
  return parseEmailList(value).join(',');
}

interface ValidateEmailListOptions {
  /** Số email tối đa cho phép trong một ô. */
  max?: number;
}

/**
 * Kiểm tra một ô nhiều email.
 *
 * Trả `null` = hợp lệ. Ô rỗng cũng hợp lệ (hầu hết các ô email này là tuỳ chọn).
 * Ngược lại trả thông báo tiếng Việt hiển thị thẳng dưới ô nhập.
 */
export function validateEmailList(
  value: string | null | undefined,
  options: ValidateEmailListOptions = {},
): string | null {
  const max = options.max ?? MAX_EMAILS_PER_FIELD;
  const emails = parseEmailList(value);
  if (emails.length === 0) return null;
  if (emails.length > max) return `Tối đa ${max} email, cách nhau bằng dấu phẩy`;
  const invalid = emails.find((email) => !isSingleEmailValid(email));
  if (invalid) return `Email không hợp lệ: ${invalid}`;
  return null;
}

/**
 * Rule antd cho ô nhiều email. Dùng trực tiếp trong `rules={[emailListRule()]}`.
 */
export function emailListRule(max: number = MAX_EMAILS_PER_FIELD): Rule {
  return {
    validator: (_, value) => {
      const error = validateEmailList(typeof value === 'string' ? value : '', { max });
      return error ? Promise.reject(new Error(error)) : Promise.resolve();
    },
  };
}

/** Placeholder dùng chung cho các ô email nhận hoá đơn. */
export const EMAIL_LIST_PLACEHOLDER = 'email1@company.com, email2@company.com';

/** Gợi ý hiển thị dưới ô nhập (antd `extra`) hoặc trong tooltip. */
export const EMAIL_LIST_HINT = `Có thể nhập nhiều email, cách nhau bằng dấu phẩy (tối đa ${MAX_EMAILS_PER_FIELD})`;
